import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

const MODES = ["In-Person", "Phone", "WhatsApp", "Video", "Other"];

const STATUSES = [
  "Pending Review",
  "Scheduled",
  "Changes Requested",
  "Completed",
  "Cancelled",
  "No Show",
];

const emptyForm = {
  leadId: "",
  scheduledAt: "",
  mode: "In-Person",
  discussion: "",
  studentRequirements: "",
  parentResponse: "",
  courseInterest: "",
  programInterest: "",
  counsellorNotes: "",
  nextAction: "",
  nextFollowUpAt: "",
};

const getId = (value) =>
  value && typeof value === "object" ? value._id : value || "";

const getPersonName = (person) => {
  if (!person) return "—";
  if (typeof person === "string") return person;

  return (
    person.name ||
    person.fullName ||
    person.email ||
    "Unnamed user"
  );
};

const getLeadName = (lead) => {
  if (!lead) return "Unnamed Lead";

  return (
    lead.name ||
    [lead.firstName, lead.lastName].filter(Boolean).join(" ") ||
    "Unnamed Lead"
  );
};

const getLeadPhone = (lead) =>
  lead?.phone || lead?.mobile || lead?.whatsapp || "—";

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toInputDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const localDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );

  return localDate.toISOString().slice(0, 16);
};

const getStatusColor = (status) => {
  switch (status) {
    case "Scheduled":
      return "primary";
    case "Pending Review":
      return "warning";
    case "Changes Requested":
      return "danger";
    case "Completed":
      return "success";
    case "Cancelled":
      return "secondary";
    case "No Show":
      return "dark";
    default:
      return "light";
  }
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getList = (data, keys = []) => {
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
};

const Counselling = () => {
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [reviewSession, setReviewSession] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    action: "Approve",
    counsellorId: "",
    participants: [],
    reviewNote: "",
    scheduledAt: "",
  });

  const [completionSession, setCompletionSession] = useState(null);
  const [completionForm, setCompletionForm] = useState({
    discussion: "",
    studentRequirements: "",
    parentResponse: "",
    courseInterest: "",
    programInterest: "",
    counsellorNotes: "",
    nextAction: "",
    nextFollowUpAt: "",
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [sessionResponse, leadResponse, statsResponse] =
        await Promise.all([
          api.get("/counselling-sessions", {
            params: { limit: 200, page: 1 },
          }),
          api.get("/leads", {
            params: { limit: 200 },
          }),
          api.get("/counselling-sessions/stats"),
        ]);

      const sessionData = sessionResponse?.data?.data;
      const leadData = leadResponse?.data?.data;

      setSessions(
        getList(sessionData, ["sessions", "items"])
      );

      setLeads(
        getList(leadData, ["leads", "items"])
      );

      setStats(statsResponse?.data?.data || {});
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to load counselling sessions."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadUsers = async () => {
    const response = await api.get("/organization/users");
    const data = response?.data?.data;

    const list = getList(data, ["users", "members"]);

    setUsers(list.filter((user) => user.isActive !== false));

    return list.filter((user) => user.isActive !== false);
  };

  const eligibleUsers = useMemo(
    () =>
      users.filter((user) =>
        ["Counsellor", "Staff"].includes(user.role)
      ),
    [users]
  );

  const getSessionLead = (session) => {
    if (
      session?.leadId &&
      typeof session.leadId === "object"
    ) {
      return session.leadId;
    }

    return leads.find(
      (lead) =>
        String(lead._id) === String(getId(session?.leadId))
    );
  };

  const filteredSessions = useMemo(() => {
    const query = search.trim().toLowerCase();

    return sessions.filter((session) => {
      const lead = getSessionLead(session);

      const matchesSearch =
        !query ||
        getLeadName(lead).toLowerCase().includes(query) ||
        getLeadPhone(lead).toLowerCase().includes(query) ||
        String(session.discussion || "").toLowerCase().includes(query) ||
        getPersonName(session.counsellorId)
          .toLowerCase()
          .includes(query) ||
        getPersonName(session.createdBy)
          .toLowerCase()
          .includes(query);

      const matchesStatus =
        statusFilter === "all" ||
        session.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sessions, leads, search, statusFilter]);

  const summary = useMemo(
    () => ({
      total: sessions.length,
      pendingReview: sessions.filter(
        (item) => item.status === "Pending Review"
      ).length,
      scheduled: sessions.filter(
        (item) => item.status === "Scheduled"
      ).length,
      completed: sessions.filter(
        (item) => item.status === "Completed"
      ).length,
      changesRequested: sessions.filter(
        (item) => item.status === "Changes Requested"
      ).length,
    }),
    [sessions]
  );

  const openCreate = () => {
    setEditingSession(null);
    setForm({
      ...emptyForm,
      scheduledAt: toInputDateTime(
        Date.now() + 60 * 60 * 1000
      ),
    });
    setError("");
    setSuccess("");
    setFormOpen(true);
  };

  const openEdit = (session) => {
    setEditingSession(session);

    setForm({
      leadId: getId(session.leadId),
      scheduledAt: toInputDateTime(session.scheduledAt),
      mode: session.mode || "In-Person",
      discussion: session.discussion || "",
      studentRequirements: session.studentRequirements || "",
      parentResponse: session.parentResponse || "",
      courseInterest: session.courseInterest || "",
      programInterest: session.programInterest || "",
      counsellorNotes: session.counsellorNotes || "",
      nextAction: session.nextAction || "",
      nextFollowUpAt: toInputDateTime(session.nextFollowUpAt),
    });

    setError("");
    setFormOpen(true);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const saveSession = async (event) => {
    event.preventDefault();

    if (!form.leadId || !form.scheduledAt) {
      setError("Select a lead and counselling date/time.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        ...form,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
        nextFollowUpAt: form.nextFollowUpAt
          ? new Date(form.nextFollowUpAt).toISOString()
          : null,
      };

      // Staff requests do not assign counsellors here.
      // Admin/Supervisor review assigns them separately.
      if (editingSession?._id) {
        await api.put(
          `/counselling-sessions/${editingSession._id}`,
          payload
        );
        setSuccess("Counselling session updated.");
      } else {
        await api.post("/counselling-sessions", payload);
        setSuccess("Counselling request submitted for review.");
      }

      setFormOpen(false);
      setEditingSession(null);
      await loadData();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to save counselling session."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const openReview = async (session) => {
    try {
      setError("");
      await loadUsers();

      setReviewSession(session);
      setReviewForm({
        action: "Approve",
        counsellorId: getId(session.counsellorId),
        participants: (session.participants || []).map(getId),
        reviewNote: session.reviewNote || "",
        scheduledAt: toInputDateTime(session.scheduledAt),
      });
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Unable to load organization users."
        )
      );
    }
  };

  const submitReview = async (event) => {
    event.preventDefault();

    if (
      reviewForm.action === "Approve" &&
      !reviewForm.counsellorId
    ) {
      setError("Select a primary counsellor before approving.");
      return;
    }

    if (
      reviewForm.action === "Request Changes" &&
      !reviewForm.reviewNote.trim()
    ) {
      setError("Enter a note explaining the requested changes.");
      return;
    }

    try {
      setReviewSaving(true);
      setError("");
      setSuccess("");

      await api.put(
        `/counselling-sessions/${reviewSession._id}/review`,
        {
          action: reviewForm.action,
          counsellorId:
            reviewForm.action === "Approve"
              ? reviewForm.counsellorId
              : undefined,
          participants:
            reviewForm.action === "Approve"
              ? reviewForm.participants
              : undefined,
          reviewNote: reviewForm.reviewNote,
          scheduledAt: reviewForm.scheduledAt
            ? new Date(reviewForm.scheduledAt).toISOString()
            : undefined,
        }
      );

      setSuccess(
        reviewForm.action === "Approve"
          ? "Counselling approved and staff assigned."
          : "Changes requested successfully."
      );

      setReviewSession(null);
      await loadData();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to process counselling review."
        )
      );
    } finally {
      setReviewSaving(false);
    }
  };

  const openCompletion = (session) => {
    setCompletionSession(session);
    setCompletionForm({
      discussion: session.discussion || "",
      studentRequirements: session.studentRequirements || "",
      parentResponse: session.parentResponse || "",
      courseInterest: session.courseInterest || "",
      programInterest: session.programInterest || "",
      counsellorNotes: session.counsellorNotes || "",
      nextAction: session.nextAction || "",
      nextFollowUpAt: toInputDateTime(session.nextFollowUpAt),
    });
  };

  const completeSession = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await api.put(
        `/counselling-sessions/${completionSession._id}/complete`,
        {
          ...completionForm,
          nextFollowUpAt: completionForm.nextFollowUpAt
            ? new Date(
                completionForm.nextFollowUpAt
              ).toISOString()
            : null,
        }
      );

      setCompletionSession(null);
      setSuccess("Counselling session completed.");
      await loadData();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to complete counselling session."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const cancelSession = async (session) => {
    const reason = window.prompt(
      "Enter a cancellation reason (optional):"
    );

    if (reason === null) return;

    try {
      setError("");
      setSuccess("");

      await api.put(
        `/counselling-sessions/${session._id}/cancel`,
        { reason }
      );

      setSuccess("Counselling session cancelled.");
      await loadData();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to cancel counselling session."
        )
      );
    }
  };

  const markNoShow = async (session) => {
    const note = window.prompt("Enter a note (optional):");

    if (note === null) return;

    try {
      setError("");
      setSuccess("");

      await api.put(
        `/counselling-sessions/${session._id}/no-show`,
        { note }
      );

      setSuccess("Session marked as no-show.");
      await loadData();
    } catch (requestError) {
      setError(
        getErrorMessage(
          requestError,
          "Failed to mark session as no-show."
        )
      );
    }
  };

  const openLead = (session) => {
    const leadId = getId(session?.leadId);
    if (leadId) navigate(`/leads/${leadId}`);
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Counselling" pageTitle="Leads" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="text-muted mt-2">
              Loading counselling sessions...
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title =
    "Counselling | eduLeads.Online";

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Counselling" pageTitle="Leads" />

          {error && (
            <Alert color="danger" toggle={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" toggle={() => setSuccess("")}>
              {success}
            </Alert>
          )}
              <Row>
                    <Col lg={12}>
                      <Card>
                        <CardHeader>
                          <div className="d-flex align-items-center flex-wrap gap-2">
                            <div className="flex-grow-1">
                              <h5 className="mb-0">
                                Counselling
                              </h5>
          
                              <small className="text-muted">
                                Submit requests, review assignments and manage sessions.
                              </small>
                            </div>
          
                            <div className="hstack gap-2">
                                <Button color="primary" onClick={openCreate}>
                <i className="ri-add-line me-1" />
                New Counselling Request
              </Button>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    </Col>
                  </Row>

          <Row className="g-3 mb-3">
            {[
              ["Total", summary.total, "primary"],
              ["Pending Review", summary.pendingReview, "warning"],
              ["Scheduled", summary.scheduled, "info"],
              ["Changes Requested", summary.changesRequested, "danger"],
              ["Completed", summary.completed, "success"],
            ].map(([label, value, color]) => (
              <Col xl={true} md={4} sm={6} key={label}>
                <Card className="mb-0 h-100">
                  <CardBody>
                    <div className="text-muted mb-1">{label}</div>
                    <h4 className={`mb-0 text-${color}`}>{value}</h4>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Card>
            <CardBody>
              <Row className="g-3">
                <Col md={8}>
                  <Label>Search</Label>
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search lead, phone, counsellor or notes..."
                  />
                </Col>
                <Col md={4}>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(event) =>
                      setStatusFilter(event.target.value)
                    }
                  >
                    <option value="all">All statuses</option>
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between mb-3">
                <h5 className="mb-0">Counselling Sessions</h5>
                <Button
                  color="light"
                  size="sm"
                  onClick={loadData}
                >
                  <i className="ri-refresh-line me-1" />
                  Refresh
                </Button>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Student / Lead</th>
                      <th>Scheduled</th>
                      <th>Status</th>
                      <th>Requested By</th>
                      <th>Primary Counsellor</th>
                      <th>Supporting Staff</th>
                      <th>Review</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.length === 0 ? (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center text-muted py-5"
                        >
                          No counselling sessions found.
                        </td>
                      </tr>
                    ) : (
                      filteredSessions.map((session) => {
                        const lead = getSessionLead(session);
                        const participants = session.participants || [];
                        const canReview = [
                          "Pending Review",
                          "Changes Requested",
                        ].includes(session.status);

                        return (
                          <tr key={session._id}>
                            <td>
                              <button
                                type="button"
                                className="btn btn-link p-0 fw-semibold text-start"
                                onClick={() => openLead(session)}
                              >
                                {getLeadName(lead)}
                              </button>
                              <div className="small text-muted">
                                {getLeadPhone(lead)}
                              </div>
                            </td>

                            <td>{formatDate(session.scheduledAt)}</td>

                            <td>
                              <Badge color={getStatusColor(session.status)}>
                                {session.status}
                              </Badge>
                              <div className="small text-muted mt-1">
                                {session.mode}
                              </div>
                            </td>

                            <td>{getPersonName(session.createdBy)}</td>

                            <td>
                              {getPersonName(session.counsellorId)}
                            </td>

                            <td>
                              {participants.length
                                ? participants
                                    .map(getPersonName)
                                    .join(", ")
                                : "—"}
                            </td>

                            <td>
                              <div>{getPersonName(session.reviewedBy)}</div>
                              {session.reviewedAt && (
                                <div className="small text-muted">
                                  {formatDate(session.reviewedAt)}
                                </div>
                              )}
                              {session.reviewNote && (
                                <div
                                  className="small text-muted"
                                  title={session.reviewNote}
                                >
                                  {session.reviewNote.length > 60
                                    ? `${session.reviewNote.slice(0, 60)}…`
                                    : session.reviewNote}
                                </div>
                              )}
                            </td>

                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  color="light"
                                  size="sm"
                                  title="View lead"
                                  onClick={() => openLead(session)}
                                >
                                  <i className="ri-eye-line" />
                                </Button>

                                {canReview && (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => openReview(session)}
                                  >
                                    Review / Assign
                                  </Button>
                                )}

                                {[
                                  "Pending Review",
                                  "Changes Requested",
                                  "Scheduled",
                                ].includes(session.status) && (
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => openEdit(session)}
                                    title="Edit session details"
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                )}

                                {session.status === "Scheduled" && (
                                  <>
                                    <Button
                                      color="success"
                                      size="sm"
                                      title="Complete"
                                      onClick={() => openCompletion(session)}
                                    >
                                      <i className="ri-check-line" />
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      title="Mark no-show"
                                      onClick={() => markNoShow(session)}
                                    >
                                      No-show
                                    </Button>
                                    <Button
                                      color="danger"
                                      outline
                                      size="sm"
                                      title="Cancel"
                                      onClick={() => cancelSession(session)}
                                    >
                                      <i className="ri-close-line" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Create / Edit session */}
      <Modal
        isOpen={formOpen}
        toggle={() => !saving && setFormOpen(false)}
        centered
        size="lg"
      >
        <Form onSubmit={saveSession}>
          <ModalHeader toggle={() => !saving && setFormOpen(false)}>
            {editingSession ? "Edit Counselling Session" : "New Counselling Request"}
          </ModalHeader>

          <ModalBody>
            <Alert color="info">
              Your request is recorded separately from the lead assignment.
              Admin/Supervisor reviews the request and assigns counselling staff.
            </Alert>

            <Row className="g-3">
              <Col md={6}>
                <Label>Student / Lead *</Label>
                <Input
                  type="select"
                  name="leadId"
                  value={form.leadId}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select lead</option>
                  {leads.map((lead) => (
                    <option key={lead._id} value={lead._id}>
                      {getLeadName(lead)}
                      {lead.leadNumber ? ` — ${lead.leadNumber}` : ""}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={6}>
                <Label>Date & Time *</Label>
                <Input
                  type="datetime-local"
                  name="scheduledAt"
                  value={form.scheduledAt}
                  onChange={handleFormChange}
                  required
                />
              </Col>

              <Col md={6}>
                <Label>Mode</Label>
                <Input
                  type="select"
                  name="mode"
                  value={form.mode}
                  onChange={handleFormChange}
                >
                  {MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </Input>
              </Col>

              {[
                ["discussion", "Discussion"],
                ["studentRequirements", "Student Requirements"],
                ["parentResponse", "Parent Response"],
                ["courseInterest", "Course Interest"],
                ["programInterest", "Program Interest"],
                ["counsellorNotes", "Counsellor Notes"],
                ["nextAction", "Next Action"],
              ].map(([name, label]) => (
                <Col md={6} key={name}>
                  <Label>{label}</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    name={name}
                    value={form[name]}
                    onChange={handleFormChange}
                  />
                </Col>
              ))}

              <Col md={6}>
                <Label>Next Follow-up</Label>
                <Input
                  type="datetime-local"
                  name="nextFollowUpAt"
                  value={form.nextFollowUpAt}
                  onChange={handleFormChange}
                />
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="light"
              disabled={saving}
              onClick={() => setFormOpen(false)}
            >
              Close
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Save Request"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Admin / Supervisor review and assignment */}
      <Modal
        isOpen={Boolean(reviewSession)}
        toggle={() => !reviewSaving && setReviewSession(null)}
        centered
        size="lg"
      >
        <Form onSubmit={submitReview}>
          <ModalHeader toggle={() => !reviewSaving && setReviewSession(null)}>
            Review & Assign Counselling
          </ModalHeader>

          <ModalBody>
            <Alert color="info">
              Assigning counselling staff does not change the lead's existing
              assigned staff member.
            </Alert>

            <Row className="g-3">
              <Col md={6}>
                <Label>Decision *</Label>
                <Input
                  type="select"
                  value={reviewForm.action}
                  onChange={(event) =>
                    setReviewForm((previous) => ({
                      ...previous,
                      action: event.target.value,
                    }))
                  }
                >
                  <option value="Approve">Approve & Assign</option>
                  <option value="Request Changes">Request Changes</option>
                </Input>
              </Col>

              <Col md={6}>
                <Label>Scheduled Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={reviewForm.scheduledAt}
                  onChange={(event) =>
                    setReviewForm((previous) => ({
                      ...previous,
                      scheduledAt: event.target.value,
                    }))
                  }
                />
              </Col>

              {reviewForm.action === "Approve" && (
                <>
                  <Col md={12}>
                    <Label>Primary Counsellor *</Label>
                    <Input
                      type="select"
                      value={reviewForm.counsellorId}
                      onChange={(event) =>
                        setReviewForm((previous) => ({
                          ...previous,
                          counsellorId: event.target.value,
                        }))
                      }
                      required
                    >
                      <option value="">Select primary counsellor</option>
                      {eligibleUsers.map((user) => (
                        <option key={user._id} value={user._id}>
                          {getPersonName(user)} — {user.role}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md={12}>
                    <Label>Supporting Staff (optional)</Label>
                    <Input
                      type="select"
                      multiple
                      value={reviewForm.participants}
                      onChange={(event) => {
                        const selected = Array.from(
                          event.target.selectedOptions,
                          (option) => option.value
                        );

                        setReviewForm((previous) => ({
                          ...previous,
                          participants: selected,
                        }));
                      }}
                    >
                      {eligibleUsers
                        .filter(
                          (user) =>
                            String(user._id) !==
                            String(reviewForm.counsellorId)
                        )
                        .map((user) => (
                          <option key={user._id} value={user._id}>
                            {getPersonName(user)} — {user.role}
                          </option>
                        ))}
                    </Input>
                    <div className="form-text">
                      Hold Ctrl (Windows) or Command (Mac) to select multiple
                      staff members.
                    </div>
                  </Col>
                </>
              )}

              <Col md={12}>
                <Label>
                  {reviewForm.action === "Request Changes"
                    ? "Reason for Changes *"
                    : "Review Note"}
                </Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={reviewForm.reviewNote}
                  onChange={(event) =>
                    setReviewForm((previous) => ({
                      ...previous,
                      reviewNote: event.target.value,
                    }))
                  }
                  required={reviewForm.action === "Request Changes"}
                />
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="light"
              disabled={reviewSaving}
              onClick={() => setReviewSession(null)}
            >
              Close
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={reviewSaving}
            >
              {reviewSaving ? <Spinner size="sm" /> : "Submit Review"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Completion details */}
      <Modal
        isOpen={Boolean(completionSession)}
        toggle={() => !saving && setCompletionSession(null)}
        centered
        size="lg"
      >
        <Form onSubmit={completeSession}>
          <ModalHeader toggle={() => !saving && setCompletionSession(null)}>
            Complete Counselling Session
          </ModalHeader>

          <ModalBody>
            <Row className="g-3">
              {[
                ["discussion", "Discussion"],
                ["studentRequirements", "Student Requirements"],
                ["parentResponse", "Parent Response"],
                ["courseInterest", "Course Interest"],
                ["programInterest", "Program Interest"],
                ["counsellorNotes", "Counsellor Notes"],
                ["nextAction", "Next Action"],
              ].map(([name, label]) => (
                <Col md={6} key={name}>
                  <Label>{label}</Label>
                  <Input
                    type="textarea"
                    rows="2"
                    value={completionForm[name]}
                    onChange={(event) =>
                      setCompletionForm((previous) => ({
                        ...previous,
                        [name]: event.target.value,
                      }))
                    }
                  />
                </Col>
              ))}

              <Col md={6}>
                <Label>Next Follow-up</Label>
                <Input
                  type="datetime-local"
                  value={completionForm.nextFollowUpAt}
                  onChange={(event) =>
                    setCompletionForm((previous) => ({
                      ...previous,
                      nextFollowUpAt: event.target.value,
                    }))
                  }
                />
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="light"
              disabled={saving}
              onClick={() => setCompletionSession(null)}
            >
              Close
            </Button>
            <Button type="submit" color="success" disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Complete Session"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default Counselling;